import { memo } from 'react'

import { Offer } from '@wwt/entities/search-response'

import { ImageSlider } from '@/features/Image'
import { cn } from '@/shared/lib/cn'
import { LinkBox, LinkOverlay } from '@/shared/ui/link/index.ts'

import { useOfferCard } from '../model/useOfferCard.ts'
import { useOfferCardReservation } from '../model/useOfferCardReservation.ts'
import { OfferCardHighRatingTag } from './OfferCardElements/OfferCardHighRatingTag.tsx'
import { OfferCardHotelNameWithRating } from './OfferCardElements/OfferCardHotelNameWithRating.tsx'
import { OfferCardTitle } from './OfferCardElements/OfferCardTitle.tsx'

interface OfferCardReservationProps {
	offer: Offer
	className?: string
	onClick: () => void
}

export const OfferCardReservation = memo(
	({ offer, className, onClick }: OfferCardReservationProps) => {
		const { images } = useOfferCard(offer, undefined)
		const { roomOfferPath, price, country, dates, onLinkClick } =
			useOfferCardReservation(offer, onClick)

		return (
			<LinkBox
				className={cn(
					'relative flex h-full w-full max-w-[21rem] flex-col rounded-md bg-white hover:shadow-md md:max-w-[17.5rem] md:min-w-[17.5rem]',
					className
				)}
				data-wwt-id="offer-card--unique"
				onClick={onLinkClick}
			>
				<div className="mb-4 flex flex-col gap-y-2 px-3 pt-1">
					<div className="ts-headline-4">{country}</div>
					<div className="ts-4">{dates}</div>
				</div>

				<ImageSlider
					classNames={{
						base: 'h-[16rem] min-h-[16rem] w-full overflow-hidden rounded-md'
					}}
					images={images}
					to={roomOfferPath}
					onImageClick={onClick}
				/>

				<div className="flex flex-1 flex-col px-3 pt-6 pb-2">
					<div className="flex flex-1 flex-col justify-evenly gap-4">
						<OfferCardHighRatingTag />

						<div className="flex w-fit flex-col gap-1 text-gray-400">
							<LinkOverlay
								className="ts-5 hover:text-brand-200 inline-block h-full"
								data-wwt-id="offer-card__title-link--unique"
								to={roomOfferPath}
							>
								<OfferCardTitle
									offer={offer}
									dateIntervalClassName="hidden"
								/>
							</LinkOverlay>

							<OfferCardHotelNameWithRating offer={offer} />
						</div>
					</div>

					<hr className="mt-2 block w-full border-t-0 border-b border-solid opacity-[60%] md:mt-3" />

					<div
						className="ts-3 mt-3 text-gray-500"
						title={price}
					>
						{price}
					</div>
				</div>
			</LinkBox>
		)
	}
)

OfferCardReservation.displayName = 'OfferCardDefault'
